import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "./api";
import { Navigate, useLocation } from "react-router-dom";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try { return JSON.parse(localStorage.getItem("sh_user") || "null"); } catch { return null; }
  });
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const token = localStorage.getItem("sh_token");
    if (!token) { setUser(null); setLoading(false); return; }
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      localStorage.setItem("sh_user", JSON.stringify(res.data));
    } catch {
      setUser(null);
      localStorage.removeItem("sh_token");
      localStorage.removeItem("sh_user");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // heartbeat every 30s
  useEffect(() => {
    if (!user) return;
    const id = setInterval(() => {
      api.post("/auth/heartbeat").catch(()=>{});
    }, 30000);
    api.post("/auth/heartbeat").catch(()=>{});
    return () => clearInterval(id);
  }, [user]);

  const login = async (email, password, role) => {
    const res = await api.post("/auth/login", { email, password });
    const { token, user: u } = res.data;
    if (role && u.role !== role) {
      throw new Error(`This account is not a ${role}. Please use the correct login page.`);
    }
    localStorage.setItem("sh_token", token);
    localStorage.setItem("sh_user", JSON.stringify(u));
    localStorage.setItem("sh_role", u.role);
    setUser(u);
    return u;
  };

  const logout = () => {
    localStorage.removeItem("sh_token");
    localStorage.removeItem("sh_user");
    setUser(null);
  };

  const updateUser = (patch) => {
    const nu = { ...(user || {}), ...patch };
    setUser(nu);
    localStorage.setItem("sh_user", JSON.stringify(nu));
  };

  return (
    <AuthCtx.Provider value={{ user, loading, login, logout, refresh, updateUser }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);

export function ProtectedRoute({ children, role }) {
  const { user, loading } = useAuth();
  const loc = useLocation();
  if (loading) return <div className="p-10 text-sm text-gray-500">Loading…</div>;
  if (!user) return <Navigate to={role === "admin" ? "/admin/login" : "/executive/login"} state={{ from: loc }} replace />;
  if (role && user.role !== role) return <Navigate to={user.role === "admin" ? "/admin/dashboard" : "/executive/dashboard"} replace />;
  return children;
}
